

import React from 'react';
import { Language } from '../types';
import { TRANSLATIONS } from '../translations';

interface ServicesProps {
  language: Language;
}

export const Services: React.FC<ServicesProps> = ({ language }) => {
  const t = TRANSLATIONS[language].services;

  return (
    <div className="pt-32 lg:pt-36 bg-slate-50">
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-[10px] font-black text-blue-600 uppercase tracking-[0.4em] mb-12">{t.tag}</h2>
          
          <div className="grid lg:grid-cols-2 gap-20 mb-24">
            <div>
              <h1 className="text-5xl font-black text-slate-900 mb-8 tracking-tighter">{t.title}</h1>
              <p className="text-lg text-slate-500 mb-12 leading-relaxed">
                {t.desc}
              </p>
              <div className="space-y-4">
                {t.list.map((s: string, i: number) => (
                  <div key={i} className="flex items-center gap-4 p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
                    <i className="fa-solid fa-check text-blue-600"></i>
                    <span className="text-sm font-bold text-slate-900 uppercase tracking-widest">{s}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-slate-900 p-16 rounded-[3rem] text-white self-start">
              <h2 className="text-3xl font-black mb-8">{t.groundTitle}</h2>
              <p className="text-blue-500 font-black uppercase tracking-widest text-[10px] mb-8">{t.groundTag}</p>
              <div className="space-y-8">
                {t.groundList.map((s: any, i: number) => (
                  <div key={i}>
                    <h4 className="font-bold text-white mb-2">{s.title}</h4>
                    <p className="text-slate-400 text-sm leading-relaxed">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* New Feasibility Package Section */}
          <div className="mb-24">
            <div className="bg-blue-600 rounded-[3rem] overflow-hidden relative text-white">
               <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -mr-32 -mt-32"></div>
               <div className="p-10 lg:p-20 relative z-10 grid lg:grid-cols-2 gap-16 items-center">
                  <div>
                    <div className="inline-block px-3 py-1 bg-white/20 backdrop-blur-md rounded text-[10px] font-black uppercase tracking-widest mb-6">
                      {t.feasibility.tag}
                    </div>
                    <h2 className="text-3xl lg:text-5xl font-black tracking-tight mb-6">{t.feasibility.title}</h2>
                    <p className="text-blue-100 text-lg leading-relaxed mb-8 max-w-xl">
                      {t.feasibility.desc}
                    </p>
                    <div className="bg-white text-slate-900 inline-block px-8 py-4 rounded-xl shadow-lg">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Package Cost</p>
                      <p className="text-3xl font-black">{t.feasibility.price}</p>
                    </div>
                  </div>

                  <div className="bg-white/10 backdrop-blur-sm border border-white/10 rounded-3xl p-10">
                    <h3 className="text-xl font-bold mb-6">What's Included</h3>
                    <div className="space-y-4">
                      {t.feasibility.features.map((feature: string, idx: number) => (
                         <div key={idx} className="flex gap-4 items-center">
                           <div className="w-6 h-6 rounded-full bg-blue-400 flex items-center justify-center text-blue-900 text-xs shadow-sm">
                             <i className="fa-solid fa-check"></i>
                           </div>
                           <span className="font-medium">{feature}</span>
                         </div>
                      ))}
                    </div>
                  </div>
               </div>
            </div>
          </div>

          {/* New Business Models Section */}
          <div>
            <h2 className="text-3xl font-black text-slate-900 mb-12 tracking-tight text-center">{t.modelsTitle}</h2>
            <div className="grid md:grid-cols-2 gap-8">
               {t.models.map((model: any, idx: number) => (
                 <div key={idx} className="bg-white p-10 rounded-[2.5rem] border border-slate-200 shadow-xl hover:-translate-y-1 transition-transform duration-300">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-2xl mb-8 ${idx === 0 ? 'bg-indigo-50 text-indigo-600' : 'bg-emerald-50 text-emerald-600'}`}>
                       <i className={`fa-solid ${idx === 0 ? 'fa-diagram-project' : 'fa-handshake-simple'}`}></i>
                    </div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3">{model.tag}</div>
                    <h3 className="text-2xl font-black text-slate-900 mb-4">{model.title}</h3>
                    <div className="inline-block px-4 py-2 bg-slate-100 rounded-lg text-xs font-bold text-slate-700 mb-8">
                       {model.fee}
                    </div>
                    <div className="space-y-3">
                       {model.features.map((feat: string, fIdx: number) => (
                         <div key={fIdx} className="flex gap-3 items-start">
                            <i className="fa-solid fa-angle-right text-slate-300 mt-1"></i>
                            <span className="text-sm text-slate-600 font-medium">{feat}</span>
                         </div>
                       ))}
                    </div>
                 </div>
               ))}
            </div>
          </div>

        </div>
      </section>
    </div>
  );
};
